import React from 'react';
import type { GroundControlLocation, Inspection } from '../../types';
import { formatDate } from '../../utils/scheduling';

interface Props {
  location: GroundControlLocation;
  inspections: Inspection[];
  onClose: () => void;
}

export default function HistoryOverlay({ location, inspections, onClose }: Props) {
  const history = inspections
    .filter((i) => i.Area === location.Area && i.AreaType === location.AreaType)
    .sort((a, b) => b.DateInspected.localeCompare(a.DateInspected));

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-10 pb-6 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] flex flex-col slide-in-up">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-[#4C9E00]">Inspection History</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {location.Area} · {location.AreaType} · {history.length} record{history.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {history.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              No inspection history found for this location.
            </div>
          ) : (
            history.map((insp, i) => (
              <div
                key={i}
                className={`border rounded-xl p-4 ${
                  i === 0
                    ? 'border-[#4C9E00]/30 bg-[#EBF7E0]/30'
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">
                        {formatDate(insp.DateInspected)}
                      </span>
                      {i === 0 && (
                        <span className="bg-[#4C9E00] text-white text-xs px-1.5 py-0.5 rounded-full font-medium">
                          Latest
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {insp.InspectedBy} · Supervisor: {insp.Supervisor}
                    </p>
                  </div>
                  <span
                    className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                      insp.NumberOfConcerns > 0
                        ? 'bg-red-100 text-red-700'
                        : 'bg-green-50 text-[#4C9E00]'
                    }`}
                  >
                    {insp.NumberOfConcerns} concern{insp.NumberOfConcerns !== 1 ? 's' : ''}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  <div>
                    <span className="text-gray-400">Red Taped</span>
                    <p
                      className={`font-medium ${
                        insp.RedTaped === 'Yes' ? 'text-red-600' : 'text-gray-700'
                      }`}
                    >
                      {insp.RedTaped}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-400">Highback Inspected</span>
                    <p className="font-medium text-gray-700">{insp.HighbackInspected}</p>
                  </div>
                </div>

                {insp.Comment && (
                  <p className="text-xs text-gray-500 italic mt-2 border-t border-gray-100 pt-2">
                    "{insp.Comment}"
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
